import { DELETION_RECEIPT_TEXT } from '../constants.js'
import type { Block, DeletionReceipt, SpaceEntry } from '../types.js'

export type SpacePayload = string

export type SpaceCodec = {
  decode(payload: SpacePayload): Promise<SpaceEntry[]>
  encode(blocks: SpaceEntry[]): Promise<SpacePayload>
}

export const jsonSpaceCodec: SpaceCodec = {
  async decode(payload: SpacePayload): Promise<SpaceEntry[]> {
    return decodeJsonSpacePayload(payload)
  },

  async encode(blocks: SpaceEntry[]): Promise<SpacePayload> {
    return JSON.stringify(blocks.map(block => cloneSpaceEntry(block)))
  }
}

export function decodeSpacePayload(payload: SpacePayload): Promise<SpaceEntry[]> {
  return jsonSpaceCodec.decode(payload)
}

export function decodeJsonSpacePayload(payload: SpacePayload): SpaceEntry[] {
  const data = JSON.parse(payload)
  if (!Array.isArray(data)) {
    throw new Error('Invalid space payload: expected block array')
  }

  return data.map(normalizeSpaceEntry)
}

export function encodeSpacePayload(blocks: SpaceEntry[]): Promise<SpacePayload> {
  return jsonSpaceCodec.encode(blocks)
}

export function isValidBlock(obj: any): obj is Block {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    typeof obj.text === 'string' &&
    (obj.meta === undefined || isValidMeta(obj.meta)) &&
    Array.isArray(obj.xyzw) &&
    obj.xyzw.length === 4 &&
    obj.xyzw.every((n: any) => typeof n === 'number') &&
    Array.isArray(obj.ided) &&
    obj.ided.length === 2 &&
    obj.ided.every((s: any) => typeof s === 'string') &&
    Array.isArray(obj.link) &&
    obj.link.every((s: any) => typeof s === 'string')
  )
}

function isValidMeta(meta: any): boolean {
  if (typeof meta !== 'object' || meta === null || Array.isArray(meta)) return false
  return Object.values(meta).every((value) => typeof value === 'number')
}

export function cloneBlock(block: Block): Block {
  return {
    text: block.text,
    meta: block.meta ? { ...block.meta } : undefined,
    xyzw: [...block.xyzw] as Block['xyzw'],
    ided: [...block.ided] as Block['ided'],
    link: [...block.link]
  }
}

export function isDeletionReceipt(value: unknown): value is DeletionReceipt {
  if (!value || typeof value !== 'object') return false
  const entry = value as Record<string, unknown>
  return entry.text === DELETION_RECEIPT_TEXT
    && Object.keys(entry).length === 2
    && Array.isArray(entry.ided)
    && entry.ided.length === 2
    && entry.ided.every(id => typeof id === 'string' && id.trim().length > 0)
}

export function isLiveBlock(entry: SpaceEntry): entry is Block {
  return !isDeletionReceipt(entry) && entry.meta?.deleted !== 1
}

export function createDeletionReceipt(id: string, editedId: string): DeletionReceipt {
  return { text: DELETION_RECEIPT_TEXT, ided: [id, editedId] }
}

// Shared by JSON and IndexedDB readers. Legacy deletion stamps retain their order.
export function normalizeSpaceEntry(value: unknown): SpaceEntry {
  if (isDeletionReceipt(value)) return createDeletionReceipt(...value.ided)
  if (!isValidBlock(value)) throw new Error('Invalid space payload: invalid block')
  return value.meta?.deleted === 1
    ? createDeletionReceipt(...value.ided)
    : cloneBlock(value)
}

export function cloneSpaceEntry(entry: SpaceEntry): SpaceEntry {
  return normalizeSpaceEntry(entry)
}
